import React from 'react';
import { Typography, Box, Tooltip, Stack, CircularProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatMetricValue, getValueColor } from 'utils/metricFormatters';

/**
 * MetricDisplay - An atomic component for displaying a single metric with proper formatting
 * 
 * @param {Object} props
 * @param {any} props.value - The metric value to display
 * @param {string} props.label - The label for the metric
 * @param {string} [props.metricType] - Type of metric for formatting (percentage, currency, ratio, etc.)
 * @param {string|JSX.Element} [props.tooltip] - Optional tooltip content
 * @param {string} [props.color] - Optional color based on value (success, error, etc.)
 * @param {boolean} [props.colorByValue] - Whether to color based on value (for profits/losses)
 * @param {boolean} [props.loading] - Boolean to indicate loading state
 * @param {string} [props.size] - Size variant (small, medium, large)
 * @param {Object} [props.formatOptions] - Options for formatting
 * @param {Object} [props.sx] - Additional styling
 * @returns {JSX.Element}
 */
const MetricDisplay = ({ 
  value, 
  label, 
  metricType,
  tooltip, 
  color = 'text.primary',
  colorByValue = false,
  loading = false,
  size = 'medium',
  formatOptions = {},
  sx = {} 
}) => {
  // Format the value using our utility functions
  const displayValue = metricType 
    ? formatMetricValue(metricType, value, formatOptions)
    : (value === null || value === undefined ? 'N/A' : value);
  
  // Determine color based on value if colorByValue is true
  const displayColor = colorByValue 
    ? getValueColor(value)
    : color;
  
  // Determine typography variant based on size
  const textVariant = size === 'small' ? 'body1' : 'largeText';
  
  return (
    <Box sx={{ p: 2, ...sx }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40px">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Typography 
          component="div" 
          variant={textVariant} 
          color={displayColor} 
          align="center"
        >
          {displayValue}
        </Typography>
      )}
      
      {tooltip ? (
        <Tooltip title={tooltip}>
          <Typography 
            variant="smallText" 
            alignItems="center" 
            justifyContent="center" 
            component={Stack} 
            direction="row"
          >
            {label} <InfoOutlinedIcon fontSize="small" />
          </Typography>
        </Tooltip>
      ) : (
        <Typography 
          variant="smallText" 
          alignItems="center" 
          justifyContent="center" 
          component={Stack} 
          direction="row"
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default MetricDisplay; 